import { listenable } from 'custom-listenable';
import { useState, useMemo, useCallback, useSyncExternalStore, useRef } from 'react';

var changeListenables = new Map();
var useStorageBackedState = function (initialValue, key, storage) {
    if (key === void 0) { key = null; }
    if (storage === void 0) { storage = 'localStorage' in globalThis ? localStorage : null; }
    var trulyInitialValue = useState(initialValue)[0];
    var inMemoryStorage = useMemo(function () {
        var value = trulyInitialValue;
        return {
            get: function () { return value; },
            set: function (newValue) {
                value = newValue;
            },
        };
    }, [trulyInitialValue]);
    var internalStorage = useMemo(function () {
        var core = (function () {
            if (key === null || storage === null || storage === undefined) {
                return inMemoryStorage;
            }
            var cache = {
                rawValue: null,
                value: trulyInitialValue,
            };
            return {
                get: function () {
                    var rawValue = storage.getItem(key);
                    if (rawValue === null) {
                        return trulyInitialValue;
                    }
                    if (cache.rawValue === rawValue) {
                        return cache.value;
                    }
                    try {
                        var value_1 = JSON.parse(rawValue); // @TODO: validate data in storage
                        cache.rawValue = rawValue;
                        cache.value = value_1;
                        return value_1;
                    }
                    catch (error) {
                        console.error('Corrupted storage data. Falling back to initialState.');
                        console.error(error);
                    }
                    return trulyInitialValue;
                },
                set: function (newValue) { return storage.setItem(key, JSON.stringify(newValue)); },
            };
        })();
        var changeListenable = (function () {
            var _a;
            if (key === null) {
                return listenable();
            }
            var result = (_a = changeListenables.get(key)) !== null && _a !== void 0 ? _a : listenable();
            changeListenables.set(key, result);
            return result;
        })();
        var listen = function (onChange) {
            changeListenable.addListener(onChange);
            window.addEventListener('storage', onChange);
            return function () {
                changeListenable.removeListener(onChange);
                window.removeEventListener('storage', onChange);
            };
        };
        var set = function (newValue) {
            core.set(newValue);
            changeListenable.emit();
        };
        return { get: core.get, set: set, listen: listen };
    }, [key, storage, trulyInitialValue, inMemoryStorage]);
    var subscribe = useCallback(function (onStoreChange) { return internalStorage.listen(onStoreChange); }, [internalStorage]);
    var getSnapshot = useCallback(function () { return internalStorage.get(); }, [internalStorage]);
    var getServerSnapshot = useCallback(function () { return trulyInitialValue; }, [trulyInitialValue]);
    var value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    var valueRef = useRef(value);
    valueRef.current = value;
    var setValue = useCallback(function (newValue) {
        internalStorage.set(newValue instanceof Function ? newValue(valueRef.current) : newValue);
    }, [internalStorage]);
    return [value, setValue];
};

export { useStorageBackedState };
//# sourceMappingURL=index.es.js.map
